'use client'

import type { PropsWithChildren } from 'react'
import { useEffect, useState } from 'react'
import { FiMenu, FiPlus } from 'react-icons/fi'
import { FaCaretDown } from 'react-icons/fa'
import { CSSTransition } from 'react-transition-group'
import Link from 'next/link'
import { siteLocale, Locale, notEmpty } from '@marketing-sites/utils'
import { Container } from './Container'
import type { Route } from 'next'
import { usePathname } from 'next/navigation'

type SubmenuProps = PropsWithChildren<{
  isOpen: boolean
}>

function Submenu({ isOpen, children }: SubmenuProps) {
  return (
    <CSSTransition
      in={isOpen}
      timeout={500}
      classNames={{
        enter: 'open',
        enterDone: 'open',
        exit: 'closed',
        exitDone: 'closed',
      }}
    >
      <ul className="submenu">
        {children}
        <style global jsx>
          {`
            ul.submenu {
              color: white;
              z-index: 10;
            }
            .text {
              font-family: 'helvetica neue', Helvetica, Arial, sans-serif;
              font-size: 18px;
              font-weight: 700;
              color: white;
              text-shadow: rgba(0, 0, 0, 0.4) 0px 1px 1px;
            }
            .text:hover,
            .text:focus {
              color: #f57b29;
            }
            ul.submenu {
              background-color: #f79700;
            }
            ul.submenu > li {
              line-height: 0;
              padding: 0;
              overflow: hidden;
              transition: all 0.2s ease;
            }
            ul.submenu.closed ul.submenu li,
            ul.submenu.closed ul.submenu button,
            ul.submenu.closed ul.submenu .icon {
              line-height: 0;
              padding: 0;
              height: 0;
              border: 0;
            }
            ul.submenu.open > li {
              line-height: 16px;
            }

            ul.submenu > li > button,
            ul.submenu > li > a {
              transition: all 0.2s ease;
            }

            ul.submenu.open > li > button,
            ul.submenu.open > li > a {
              padding: 16px 0 16px 16px;
            }

            ul.submenu.open > li:first-child {
              padding-top: 8px;
            }
            ul.submenu.open > li:last-child {
              padding-bottom: 8px;
            }

            ul.submenu > li > ul.submenu > li > button,
            ul.submenu > li > ul.submenu > li > a {
              padding-left: 32px;
            }

            ul.submenu > li > ul.submenu > li > ul.submenu > li > button,
            ul.submenu > li > ul.submenu > li > ul.submenu > li > a {
              padding-left: 48px;
            }

            ul.submenu > li > button > .icon {
              height: 0;
              overflow: hidden;
            }
            ul.submenu.open > li > button > .icon {
              height: auto;
              border: 1px solid white;
              border-radius: 3px;
              padding: 2px 4px;
              margin-right: 16px;
            }

            /**
             * Style tablet widths and above
             */
            @media (min-width: 768px) {
              .text {
                font-family: 'helvetica neue', Helvetica, Arial, sans-serif;
                font-size: 16px;
                font-weight: 700;
                text-shadow: rgba(0, 0, 0, 0.4) 0px 1px 1px;
              }

              ul.submenu.open > li:first-child {
                padding-top: 0;
              }
              ul.submenu.open > li:last-child {
                padding-bottom: 0;
              }

              nav > ul.submenu > li {
                transition-duration: 0.05s;
              }

              nav > ul.submenu > li:hover {
                color: #f57b29;
              }

              nav > ul.submenu > li li:hover {
                background-color: #ffca39;
              }

              nav > ul.submenu ul.submenu .text {
                font-weight: normal;
              }

              /** First Menu */
              nav ul.submenu {
                background: none;
                position: relative;
              }
              nav > ul.submenu > li {
                line-height: 16px;
                overflow: visible;
              }
              nav > ul.submenu {
                display: flex;
                flex-wrap: wrap;
              }
              nav > ul.submenu > li > button,
              nav > ul.submenu > li > a {
                padding: 11px 14px 11px 14px;
                width: auto;
              }

              nav > ul.submenu > li button .icon {
                height: auto;
                margin-left: 4px;
                border: 0;
              }

              /** Second And Third Menu, Common */
              nav > ul.submenu > li ul.submenu {
                position: absolute;
                background-color: #f79e0c;
                display: none;
                opacity: 0;
                transition: all 0.2s ease;
                border: 1px solid #df8506;
              }

              nav > ul.submenu > li ul.submenu li {
                overflow: visible;
                position: relative;
              }

              nav > ul.submenu > li > ul.submenu button .icon {
                height: auto;
                margin-left: 40px;
                margin-right: 0;
                border: 0;
                transform: rotate(270deg);
              }

              nav > ul.submenu > li ul.submenu.open {
                display: block;
                opacity: 1;
              }

              nav > ul.submenu > li ul.submenu li,
              nav > ul.submenu > li ul.submenu li {
                line-height: 16px;
              }

              /** Third Menu */
              nav > ul.submenu > li > ul.submenu > li > button,
              nav > ul.submenu > li > ul.submenu > li > a {
                padding: 10px;
              }

              nav > ul.submenu > li > ul.submenu > li > ul.submenu.open {
                position: absolute;
                left: 100%;
                top: 0;
                min-width: 200px;
              }

              nav > ul.submenu > li > ul.submenu > li > ul.submenu > li {
                line-height: 16px;
              }

              nav
                > ul.submenu
                > li
                > ul.submenu
                > li
                > ul.submenu
                > li
                > button,
              nav > ul.submenu > li > ul.submenu > li > ul.submenu > li > a {
                padding: 10px;
              }
            }
          `}
        </style>
      </ul>
    </CSSTransition>
  )
}

interface NavItem<T extends string> {
  text: string
  linkUrl: NavItemLinkUrl<T>
  submenuItems?: NavItem<T>[] | undefined
}

interface NavItemLinkUrl<T extends string> {
  __type: 'NextURL' | 'URL'
  url: Route<T>
  newTab?: boolean
}

interface NavItemWithPossibleSubmenuProps<T extends string> {
  navItem: NavItem<T>
}

function NavItemWithPossibleSubmenu<T extends string>({
  navItem,
}: NavItemWithPossibleSubmenuProps<T>) {
  const [isShowingSubmenu, setIsShowingSubmenu] = useState(false)

  if (Array.isArray(navItem.submenuItems) && navItem.submenuItems.length > 0) {
    return (
      <li
        onMouseEnter={() => {
          if (window.innerWidth >= 768) {
            setIsShowingSubmenu(true)
          }
        }}
        onMouseLeave={() => {
          if (window.innerWidth >= 768) {
            setIsShowingSubmenu(false)
          }
        }}
      >
        <button
          aria-label="Navigation Menu"
          className="flex w-full items-center justify-between"
          onClick={() => {
            if (window.innerWidth < 768) {
              setIsShowingSubmenu(!isShowingSubmenu)
            }
          }}
        >
          <NavItemLink navItem={navItem} className="text" />

          <span className="icon">
            <FiPlus className="md:hidden" />
            <FaCaretDown className="hidden md:block" />
          </span>
        </button>
        <Submenu isOpen={isShowingSubmenu}>
          {navItem.submenuItems.map((submenuItem) => (
            <NavItemWithPossibleSubmenu
              key={submenuItem.text}
              navItem={submenuItem}
            />
          ))}
        </Submenu>
      </li>
    )
  }

  return (
    <li>
      <NavItemLink navItem={navItem} className="text block" />
    </li>
  )
}

interface NavItemLinkProps<T extends string> {
  navItem: NavItem<T>
  className?: string
}

function NavItemLink<T extends string>({
  navItem,
  className,
}: NavItemLinkProps<T>): React.JSX.Element {
  const {
    linkUrl: { __type, url, newTab },
  } = navItem
  if (__type === 'NextURL' && typeof url === 'string') {
    return (
      <Link href={url} className={className}>
        {navItem.text}
      </Link>
    )
  }
  return (
    <a href={url} className={className} {...(newTab && { target: '_blank' })}>
      {navItem.text}
    </a>
  )
}

/**
 * build a polymorphic URL object that be used to build either Next/Link or regular <a> tags.
 */
function mkNavItemLinkUrl<T extends string>(
  url: Route<T>,
  newTab = false,
  isNextLink = false,
): NavItemLinkUrl<T> {
  return {
    __type: isNextLink ? 'NextURL' : 'URL',
    url,
    newTab,
  }
}

/**
 * If the locale doesn't have a standalone schools page,
 * we add some additional items to the schools menu
 */
function schoolsSubmenuItems<T extends string>(): NavItem<T>[] | undefined {
  const hasStandaloneSchoolsPage = [
    Locale.au,
    Locale.us,
    Locale.ca,
    Locale.nz,
    Locale.uk,
  ].includes(siteLocale)
  if (!hasStandaloneSchoolsPage) {
    return [
      {
        text: 'Pricing',
        linkUrl: mkNavItemLinkUrl('/schools/pricing/' as Route<T>),
      },
      {
        text: 'Testimonial',
        linkUrl: mkNavItemLinkUrl('/about/testimonials/teachers/'),
      },
    ]
  } else if ([Locale.ca, Locale.us].includes(siteLocale)) {
    return [
      {
        text: 'Administrators',
        linkUrl: mkNavItemLinkUrl('/schools/admin/'),
      },
      {
        text: 'Teachers',
        linkUrl: mkNavItemLinkUrl('/schools/'),
      },
    ]
  }
  return undefined
}

/**
 * Return a per-locale menu item for each temporary nav item that
 * you want to display as the last item of the menu
 */
function temporaryNavItemsMenu<T extends string>(): Record<string, NavItem<T>> {
  return {
    // 'en-AU': {
    //   text: 'Back to School',
    //   linkUrl: mkNavItemLinkUrl('/back-to-school')
    // }
  }
}

const bookStoreLink = {
  [Locale.au]: 'https://readingeggsshop.com.au/',
  [Locale.ca]: 'https://readingeggsshop.com/?setCurrencyId=3',
  [Locale.nz]: 'https://readingeggsshop.com.au/?setCurrencyId=2',
  [Locale.uk]: 'https://readingeggsshop.co.uk/',
  [Locale.us]: 'https://readingeggsshop.com/',
}

function navItems<T extends string>(): NavItem<T>[] {
  const homeschoolDisplayName =
    siteLocale === Locale.uk ? 'Home Education' : 'Homeschool'

  return [
    {
      text: 'Home',
      linkUrl: mkNavItemLinkUrl('/'),
    },
    {
      text: 'About',
      linkUrl: mkNavItemLinkUrl('/about/'),
      submenuItems: [
        {
          text: 'Why it works',
          linkUrl: mkNavItemLinkUrl('/about/why-it-works/'),
        },
        {
          text: 'Parents',
          linkUrl: mkNavItemLinkUrl('/about/parents/'),
        },
        {
          text: homeschoolDisplayName,
          linkUrl: mkNavItemLinkUrl('/about/homeschool/'),
        },
        {
          text: 'Free Resources',
          linkUrl: mkNavItemLinkUrl('/free-resources/'),
        },
        {
          text: 'Lessons',
          linkUrl: mkNavItemLinkUrl('#'),
          submenuItems: [
            {
              text: 'Try a Sample Lesson',
              linkUrl: mkNavItemLinkUrl('/about/sample-lesson/'),
            },
            {
              text: 'Lesson Overview',
              linkUrl: mkNavItemLinkUrl('/about/lesson-overview/'),
            },
          ],
        },
        {
          text: 'Online Library',
          linkUrl: mkNavItemLinkUrl('/childrens-books-online-library/'),
        },
        {
          text: 'Research and News',
          linkUrl: mkNavItemLinkUrl('#'),
          submenuItems: [
            {
              text: 'Research',
              linkUrl: mkNavItemLinkUrl('/about/research/'),
            },
            {
              text: 'News',
              linkUrl: mkNavItemLinkUrl('/articles/?topic=news'),
            },
          ],
        },
        {
          text: 'Why Phonics?',
          linkUrl: mkNavItemLinkUrl('/about/phonics/'),
        },
        {
          text: 'Reading Eggs Products',
          linkUrl: mkNavItemLinkUrl('#'),
          submenuItems: [
            {
              text: 'Reading Eggs Book Store',
              linkUrl: mkNavItemLinkUrl('/about/products/'),
            },
            {
              text: 'Amazon Kindle eBooks',
              linkUrl: mkNavItemLinkUrl('/about/ebooks/'),
            },
          ],
        },
        {
          text: 'Testimonials & Awards',
          linkUrl: mkNavItemLinkUrl('#'),
          submenuItems: [
            {
              text: 'Testimonials',
              linkUrl: mkNavItemLinkUrl('/about/testimonials/'),
            },
            {
              text: 'Awards',
              linkUrl: mkNavItemLinkUrl('/about/awards/'),
            },
          ],
        },
        {
          text: 'FAQs & Technical Requirements',
          linkUrl: mkNavItemLinkUrl('#'),
          submenuItems: [
            {
              text: 'FAQs',
              linkUrl: mkNavItemLinkUrl('/info/faqs/'),
            },
            {
              text: 'Technical Requirements',
              linkUrl: mkNavItemLinkUrl('/technical-requirements/'),
            },
            {
              text: 'Accessibility',
              linkUrl: mkNavItemLinkUrl('/accessibility/'),
            },
            {
              text: 'Webinars',
              linkUrl: mkNavItemLinkUrl(
                siteLocale === Locale.au
                  ? 'https://support.3plearning.com/webinar-schedules/apac/'
                  : '/webinars/',
                true,
              ),
            },
          ],
        },
        [Locale.us, Locale.ca].includes(siteLocale)
          ? {
              text: 'Affiliates',
              linkUrl: mkNavItemLinkUrl('/about/affiliates/'),
            }
          : undefined,
        {
          text: 'Contact Us',
          linkUrl: mkNavItemLinkUrl('/contact-us/'),
        },
      ].filter(notEmpty),
    },
    {
      text: 'Articles',
      linkUrl: mkNavItemLinkUrl('/articles/', false, true),
    },
    [Locale.au, Locale.nz, Locale.uk].includes(siteLocale)
      ? {
          text: 'Mathseeds Prime',
          linkUrl: mkNavItemLinkUrl('/mathseeds-prime/'),
        }
      : undefined,
    {
      text: 'Fast Phonics',
      linkUrl: mkNavItemLinkUrl('/fast-phonics-games/'),
    },
    {
      text: 'Schools',
      linkUrl: mkNavItemLinkUrl('/schools/'),
      submenuItems: schoolsSubmenuItems(),
    },
    {
      text: 'Pricing',
      linkUrl: mkNavItemLinkUrl('/pricing/'),
    },
    {
      text: 'Apps',
      linkUrl: mkNavItemLinkUrl('/apps/'),
    },
    siteLocale in bookStoreLink
      ? {
          text: 'Bookstore',
          linkUrl: mkNavItemLinkUrl(
            bookStoreLink[siteLocale as keyof typeof bookStoreLink],
            true,
          ),
        }
      : undefined,
    temporaryNavItemsMenu()[siteLocale],
  ].filter(notEmpty)
}

export function ReadingEggsNavBar() {
  const [isShowingMobileNav, setIsShowingMobileNav] = useState(false)
  const pathname = usePathname()

  function toggleMobileNav() {
    setIsShowingMobileNav(!isShowingMobileNav)
  }

  useEffect(() => {
    setIsShowingMobileNav(false)
  }, [pathname])

  return (
    <div className="relative">
      <div className="relative z-20 flex h-14 w-full items-center justify-end bg-[#ffae00] px-4 md:hidden">
        <button
          aria-label="Navigation Menu"
          className="rounded border border-white p-2 py-1"
          type="button"
          onClick={toggleMobileNav}
        >
          <FiMenu className="text-white" size={28} />
        </button>
      </div>
      <div className="from-[#fff130] to-[#f99f1f] md:border-b-[#df8506] md:border-t-[#ffbd00] md:bg-gradient-to-b">
        <Container className="px-0">
          <nav role="navigation" aria-label="Main">
            <Submenu isOpen={isShowingMobileNav}>
              {navItems().map((navItem) => (
                <NavItemWithPossibleSubmenu
                  key={navItem.text}
                  navItem={navItem}
                />
              ))}
            </Submenu>
          </nav>
        </Container>
      </div>
    </div>
  )
}
